import { useState } from 'react';
import { SOURCE_SLOTS, type SourceView } from '@xiltrion/entidad';
import type { MailRouteChoice, MailRoutesView } from '@suite/contracts';
import { Tabla, type ColumnaTabla } from '@xiltrion/ui/bloques/tabla';
import { SelectorSimple, type OpcionSelector } from '@xiltrion/ui/bloques/selectores';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Field, FieldDescription, FieldLabel } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { avisar } from '@xiltrion/ui/sonner';
import { apiPut, errorMessage } from '@/api/client';

type AppRoutes = MailRoutesView['apps'][number];
type Feature = NonNullable<AppRoutes['features']>[number];

const OFF = 'off';
const SUITE = 'suite';
const avisarError = (e: unknown) => avisar.error(errorMessage(e));
const valueOf = (r: MailRouteChoice) => (!r ? null : 'off' in r ? OFF : r.sourceId);

/**
 * Por dónde sale cada envío (GUIA §3.10): por app (la suite primero), su correo por defecto y la lista de envíos que
 * publica, cada uno con su correo saliente, el certificado si el envío lo admite, o «No enviar». Sin elegir, el envío
 * sale por el correo por defecto; un envío con padre (un paso de un flujo, bajo su categoría), como su padre.
 */
export function MailRoutes({ slug, view, sources, editable, onSaved }: { slug: string; view: MailRoutesView; sources: SourceView[]; editable: boolean; onSaved: () => Promise<unknown> }) {
  return (
    <section className="space-y-4">
      <div><h2 className="m-0 text-base font-semibold">Envíos por app</h2><p className="m-0 text-sm text-muted-foreground">Por qué correo sale cada envío de la suite y de cada app. Lo que no se elige sale por el correo por defecto de la app.</p></div>
      {view.apps.map((a) => <AppRoutesCard key={a.code} slug={slug} app={a} sources={sources} editable={editable} onSaved={onSaved} />)}
    </section>
  );
}

function AppRoutesCard({ slug, app, sources, editable, onSaved }: { slug: string; app: AppRoutes; sources: SourceView[]; editable: boolean; onSaved: () => Promise<unknown> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const outbound = sources.filter((s) => s.kind === 'EMAIL');
  const certified = sources.filter((s) => s.kind === 'CERTIFIED_EMAIL');
  const isSuite = app.code === SUITE;
  const run = (key: string, req: Promise<unknown>, ok: string) => {
    setBusy(key);
    void req.then(() => { avisar.success(ok); return onSaved(); }, avisarError).finally(() => setBusy(null));
  };
  const setMain = (kind: 'EMAIL' | 'CERTIFIED_EMAIL', sourceId: string | null) => run(`main:${kind}`, isSuite
    ? apiPut(`/entities/${slug}/suite-mail`, { sourceId })
    : apiPut(`/entities/${slug}/source-assignments`, { appCode: app.code, kind, slot: 'main', sourceId }), kind === 'EMAIL' ? 'Correo por defecto guardado' : 'Correo certificado guardado');
  const setRoute = (f: Feature, v: string | null) => run(`f:${f.code}`, apiPut(`/entities/${slug}/mail-routes`, { appCode: app.code, feature: f.code, route: v === null ? null : v === OFF ? { off: true } : { sourceId: v } }), `«${f.name}» guardado`);
  const defaultName = app.main.outbound?.name ?? (isSuite ? 'correo de Datalog' : 'sin correo');
  const parentOf = (f: Feature) => (f.parent ? app.features?.find((x) => x.code === f.parent) : undefined);
  /**
   * Sin ruta propia: «Por defecto» (sigue al correo por defecto aunque cambie; el campo dice cuál es) o, en un envío con
   * padre (justo encima), «Heredar». Elegir un correo lo fija.
   */
  const unrouted = (f: Feature) => (parentOf(f) ? 'Heredar' : 'Por defecto');
  const unroutedShown = (f: Feature) => (parentOf(f) ? 'Heredar' : `Por defecto · ${defaultName}`);
  /** Una sola lista sin títulos: los salientes, los certificados (si el envío lo admite) y «No enviar». */
  const options = (f: Feature): OpcionSelector[] => [
    ...outbound.map((s) => ({ value: s.id, label: s.name })),
    ...(f.certifiable ? certified.map((s) => ({ value: s.id, label: /certificad/i.test(s.name) ? s.name : `${s.name} · certificado` })) : []),
    { value: OFF, label: 'No enviar' },
  ];
  const grouped = !!app.features?.some((f) => f.group);
  /** Un envío con padre se agrupa con su padre, justo debajo de él. */
  const groupOf = (f: Feature) => (parentOf(f) ?? f).group ?? 'Otros';
  const columnas: ColumnaTabla<Feature>[] = [
    { key: 'name', titulo: 'Envío', valor: (f) => f.name, secundario: (f) => f.description, ordenable: false, render: (f) => (
      <span className={`flex flex-wrap items-center gap-2 ${f.parent ? 'pl-6' : ''}`}>{f.parent && <Icono nombre="mdi:subdirectory-arrow-right" className="text-muted-foreground" />}{f.name}{f.certifiable && <Badge size="sm" variant="secondary">Certificable</Badge>}</span>
    ) },
    ...(grouped ? [{ key: 'group', titulo: 'Grupo', valor: groupOf }] : []),
    { key: 'route', titulo: 'Sale por', ancho: '18rem', ordenable: false, valor: (f) => valueOf(f.route) ?? '', render: (f) => (
      <SelectorSimple size="sm" value={valueOf(f.route)} options={options(f)} limpiable textoSinValor={unrouted(f)} placeholder={unroutedShown(f)} disabled={!editable || busy === `f:${f.code}`} onChange={(v) => setRoute(f, v)} aria-label={`Correo de ${f.name}`} />
    ) },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Icono nombre={app.icon as `${string}:${string}`} />{app.name}</CardTitle>
        <CardDescription>{isSuite ? 'Lo que la suite le escribe a las personas de la entidad. Si un envío falla con el correo de la entidad, sale con el de Datalog.' : 'Lo que la app le escribe al ciudadano y a los funcionarios.'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Field>
            <FieldLabel>Correo por defecto</FieldLabel>
            {/* La suite muestra el que usa: con un solo correo es ese (elegido o no); vaciar solo tiene sentido con varios. */}
            <SelectorSimple value={app.main.outbound?.id ?? null} options={outbound.map((s) => ({ value: s.id, label: s.name }))} limpiable={!isSuite || outbound.length > 1}
              textoSinValor={isSuite ? 'Correo de Datalog' : SOURCE_SLOTS.EMAIL[0].unassigned} placeholder={isSuite ? 'Correo de Datalog' : SOURCE_SLOTS.EMAIL[0].unassigned}
              disabled={!editable || busy === 'main:EMAIL'} onChange={(v) => setMain('EMAIL', v)} />
            {!isSuite && <FieldDescription>Los envíos sin correo propio salen por aquí.</FieldDescription>}
          </Field>
          {/* Con lista de envíos, el certificado se elige envío por envío; sin ella, la app decide qué certifica con este. */}
          {!isSuite && app.features === null && (
            <Field>
              <FieldLabel>Correo certificado</FieldLabel>
              <SelectorSimple value={app.main.certified?.id ?? null} options={certified.map((s) => ({ value: s.id, label: s.name }))} limpiable textoSinValor={SOURCE_SLOTS.CERTIFIED_EMAIL[0].unassigned} placeholder={SOURCE_SLOTS.CERTIFIED_EMAIL[0].unassigned} disabled={!editable || busy === 'main:CERTIFIED_EMAIL'} onChange={(v) => setMain('CERTIFIED_EMAIL', v)} />
              <FieldDescription>Con el que la app certifica lo que ella decida, mientras no publique sus envíos.</FieldDescription>
            </Field>
          )}
        </div>
        {app.features === null ? (
          <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>{app.name} todavía no publica sus envíos: todo sale por su correo por defecto.</AlertDescription></Alert>
        ) : (
          <Tabla filas={app.features} columnas={columnas} claveFila={(f) => f.code} densa agrupar={grouped ? { por: 'group' } : undefined} vacio={<p className="m-0 text-sm text-muted-foreground">{app.name} no manda correos.</p>} />
        )}
      </CardContent>
    </Card>
  );
}
